import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEye, faPen } from "@fortawesome/free-solid-svg-icons";

import CustomForm from "../general/form/CustomForm";
import CustomFormSkeleton from "../general/auxiliar/skeleton/CustomFormSkeleton";
import OptionSelector from "../general/auxiliar/ui/OptionSelector";
import MainSortableField from "./patientHistory/MainSortableField";
import useFormWithDisable from "../../auxiliar/customHooks/useFormWithDisable";
import {
    getClinicPatientHistoryTemplate,
    putClinicPatientHistoryTemplate,
} from "../../actions/clinics";
import { fetchData } from "../../auxiliar/auxFunctions";

const PatientHistoryTemplate = () => {
    const sensors = useSensors(useSensor(PointerSensor));
    const {
        register,
        unregister,
        watch,
        errors,
        isSubmitting,
        setValue,
        handleSubmit,
        handleSubmitWithoutValidation,
    } = useFormWithDisable();

    const dispatch = useDispatch();
    const clinicId = useSelector((state) => state.activeClinic.id);

    const [fields, setFields] = useState([]);
    const [selected, setSelected] = useState("preview");
    const [previewConfig, setPreviewConfig] = useState({});
    const [customLoading, setCustomLoading] = useState(true);

    const rawFormConfig = watch();

    // Download the form config from the server
    useEffect(() => {
        const auxFectchData = async () => {
            setCustomLoading(true);
            try {
                const fetchedValues = await fetchData(
                    dispatch,
                    getClinicPatientHistoryTemplate,
                    clinicId,
                );

                if (fetchedValues?.content) {
                    setPreviewConfig(fetchedValues.content);
                    setFields(
                        fetchedValues.order.filter((field) => !field.includes("dummy")) || [],
                    );

                    // Initialize form values
                    Object.entries(fetchedValues.content).forEach(([fieldId, fieldConfig]) => {
                        setValue(fieldId, fieldConfig);
                    });
                }
            } finally {
                setCustomLoading(false);
            }
        };

        if (clinicId) {
            auxFectchData();
        }
    }, [clinicId, dispatch, setValue]);

    // This will update previewConfig whenever fields or form values change
    useEffect(() => {
        if (!fields.length) return;

        const newPreviewConfig = {};
        let accPos = 0;

        fields.forEach((fieldId) => {
            const fieldConfig = rawFormConfig[fieldId];
            if (!fieldConfig?.type) return;

            // Add dummy field for checkbox and textarea alignment if needed
            if (["checkbox", "textarea"].includes(fieldConfig.type) && accPos % 2 !== 0) {
                newPreviewConfig[`${fieldId}-dummy`] = {
                    condition: true,
                    type: "dummy",
                    position: accPos,
                };

                accPos += 1;
            }

            const name = `${fieldId}-name`;
            setValue(name, fieldConfig.defaultValue);

            // Add the field to preview config
            newPreviewConfig[fieldId] = {
                ...fieldConfig,
                position: accPos,
                condition: true,
                disabled: true,
                name: name,
                label: `${fieldConfig?.label.replace(" *", "")}${fieldConfig?.required ? " *" : ""}`,
            };

            // Calculate position based on field type
            if (fieldConfig.type === "textarea") {
                accPos += 2;
            } else {
                accPos += 1;
            }
        });

        setPreviewConfig(newPreviewConfig);
    }, [fields, JSON.stringify(rawFormConfig)]);

    const options = {
        preview: {
            condition: true,
            title: "Visualizar",
            icon: faEye,
            selected: selected === "preview",
            onClick: handleSubmitWithoutValidation(setSelected, "preview"),
        },
        builder: {
            condition: true,
            title: "Editar",
            icon: faPen,
            selected: selected === "builder",
            onClick: handleSubmitWithoutValidation(setSelected, "builder"),
        },
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            setFields((items) => {
                const oldIndex = items.indexOf(active.id);
                const newIndex = items.indexOf(over.id);
                const newFields = arrayMove(items, oldIndex, newIndex);

                // Update positions after reordering
                newFields?.forEach((field, index) => {
                    setValue(`${field}.position`, index + 1);
                });

                return newFields;
            });
        }
    };

    const handleAddField = () => {
        const newFieldId = uuidv4();
        const position = fields.length + 1;
        setFields([...fields, newFieldId]);

        // Set initial values for the new field
        setValue(`${newFieldId}.name`, `Campo ${position}`);
    };

    const handleRemoveField = (fieldToRemove) => {
        setFields(fields.filter((field) => field !== fieldToRemove));

        // Update positions of remaining fields
        const remainingFields = fields.filter((field) => field !== fieldToRemove);
        remainingFields?.forEach((field, index) => {
            setValue(`${field}.position`, index + 1);
        });

        // delete from useForms
        unregister(fieldToRemove);
    };

    return (
        <section>
            <h2 className="sr-only">
                Personaliza el formulario de historia clínica de tu clínica de fisioterapia en
                MiFisio Agenda - Historial clínico personalizado
            </h2>

            <div className="flex justify-between">
                <h3 className="text-primary text-2xl">
                    Plantilla de Historia Clínica -{" "}
                    {selected === "builder" ? "Editar" : "Visualizar"}
                </h3>

                <OptionSelector options={options} />
            </div>

            <div className="my-3">
                {selected === "preview" && (
                    <>
                        <div>
                            {customLoading ? (
                                <CustomFormSkeleton className="grid grid-cols-1 md:grid-cols-2 gap-3" />
                            ) : fields.length === 0 ? (
                                <div className="text-center text-base-content/70 py-8">
                                    No hay campos creados todavía. Añade un campo para empezar.
                                </div>
                            ) : (
                                <form>
                                    <CustomForm
                                        formConfig={previewConfig}
                                        errors={errors}
                                        register={register}
                                        className="grid grid-cols-1 md:grid-cols-2 gap-3"
                                        isLoading={customLoading}
                                    />
                                </form>
                            )}

                            <div className="flex gap-3 mt-3">
                                <button
                                    onClick={handleSubmitWithoutValidation(setSelected, "builder")}
                                    className="btn btn-primary btn-sm"
                                    disabled={isSubmitting}
                                >
                                    <span>Editar</span>
                                </button>
                                <button
                                    onClick={handleSubmit(
                                        putClinicPatientHistoryTemplate,
                                        previewConfig,
                                        clinicId,
                                    )}
                                    className="btn btn-primary btn-sm"
                                    disabled={isSubmitting}
                                >
                                    <span>Guardar</span>
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {selected === "builder" && (
                    <>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                            modifiers={[restrictToVerticalAxis]}
                        >
                            <div className="grid grid-cols-1 gap-3">
                                <SortableContext
                                    items={fields}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {customLoading ? (
                                        <CustomFormSkeleton className="grid grid-cols-1 md:grid-cols-2 gap-3" />
                                    ) : fields.length === 0 ? (
                                        <div className="text-center text-base-content/70 py-8">
                                            No hay campos creados todavía. Añade un campo para
                                            empezar.
                                        </div>
                                    ) : (
                                        fields.map((field) => (
                                            <MainSortableField
                                                key={field}
                                                id={field}
                                                position={fields.findIndex((f) => f === field) + 1}
                                                onRemove={handleSubmitWithoutValidation(
                                                    handleRemoveField,
                                                    field,
                                                )}
                                                register={register}
                                                errors={errors}
                                                watch={watch}
                                                setValue={setValue}
                                            />
                                        ))
                                    )}
                                </SortableContext>
                            </div>
                        </DndContext>

                        <div className="mt-5 flex gap-3">
                            <button
                                onClick={handleAddField}
                                className="btn btn-primary btn-sm"
                                disabled={isSubmitting}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                <span>Añadir campo</span>
                            </button>
                            <button
                                onClick={handleSubmitWithoutValidation(setSelected, "preview")}
                                className="btn btn-primary btn-sm"
                                disabled={isSubmitting}
                            >
                                <span>Visualizar</span>
                            </button>
                            <button
                                onClick={handleSubmit(
                                    putClinicPatientHistoryTemplate,
                                    previewConfig,
                                    clinicId,
                                    handleSubmitWithoutValidation(setSelected, "preview"),
                                )}
                                className="btn btn-primary btn-sm"
                                disabled={isSubmitting}
                            >
                                <span>Guardar</span>
                            </button>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default PatientHistoryTemplate;
